import type { ApiSuccess, Paginated } from './api';

export type FileMimeType = 'image/jpeg' | 'image/png' | 'image/webp' | 'application/pdf';
export type FileVisibility = 'public' | 'private';
export type FileUploadStatus = 'pending' | 'ready' | 'rejected';

/**
 * The display sizes of an image, by width in pixels. The admin app stores a WebP at each one the
 * image is at least as wide as, and a public page asks for one with `?w=` on the image's
 * `contentUrl` — the server answers with the smallest stored size that covers it, or the
 * original when none does.
 */
export const FILE_IMAGE_WIDTHS = [640, 1280, 1920] as const;
export type FileImageWidth = (typeof FILE_IMAGE_WIDTHS)[number];

export interface PublicFile {
  id: string;
  originalFilename: string;
  mimeType: FileMimeType;
  sizeBytes: number;
  visibility: 'public';
  contentUrl: string;
  createdAt: string;
}

/**
 * The photo sets a public page draws straight from a folder of the file library, so staff change
 * the pictures by changing what is in the folder. Each one names a single folder; see
 * `backend/docs/file-storage.md` for which.
 */
export const PUBLIC_FILE_GALLERIES = ['about', 'home'] as const;
export type PublicFileGallery = (typeof PUBLIC_FILE_GALLERIES)[number];

export interface AdminFile {
  id: string;
  originalFilename: string;
  mimeType: FileMimeType;
  sizeBytes: number;
  checksumSha256: string;
  visibility: FileVisibility;
  uploadStatus: FileUploadStatus;
  contentUrl: string | null;
  uploadExpiresAt: string | null;
  verifiedAt: string | null;
  rejectionReason: string | null;
  createdByUserId: string | null;
  /** The folder it lives in, or null for the root of the library. */
  folderId: string | null;
  /**
   * A short-lived signed URL for a small WebP preview, or null when none has
   * been made yet. Like any signed URL it expires; fetch the file again for a
   * fresh one rather than holding on to it.
   */
  thumbnailUrl: string | null;
  /**
   * The display sizes stored so far, ascending. Empty for a PDF, and for an image uploaded before
   * display sizes existed until the Files page makes them.
   */
  variantWidths: FileImageWidth[];
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A folder in the file library. Folders organise files; they carry no visibility of their own. */
export interface AdminFileFolder {
  id: string;
  name: string;
  /** Null for a folder at the root. */
  parentId: string | null;
  /** Live (not deleted) files directly inside it, not counting subfolders. */
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

/** One file a bulk action could not apply to, and why. */
export interface BulkFileFailure {
  fileId: string;
  code: string;
  message: string;
}

/** What a bulk action did: every file is in exactly one of the two lists. */
export interface BulkFileResult {
  succeeded: string[];
  failed: BulkFileFailure[];
}

export interface PresignedFileUpload {
  method: 'PUT';
  url: string;
  headers: Record<string, string>;
  expiresAt: string;
}

export interface InitiatedFileUpload {
  file: AdminFile;
  upload: PresignedFileUpload;
}

export interface PresignedFileDownload {
  url: string;
  expiresAt: string;
}

export type AdminFileListResponse = Paginated<AdminFile>;
export type AdminFileFolderResponse = ApiSuccess<AdminFileFolder>;
export type AdminFileFolderListResponse = ApiSuccess<AdminFileFolder[]>;
export type BulkFileResultResponse = ApiSuccess<BulkFileResult>;
export type PresignedFileUploadResponse = ApiSuccess<PresignedFileUpload>;
export type AdminFileResponse = ApiSuccess<AdminFile>;
export type InitiatedFileUploadResponse = ApiSuccess<InitiatedFileUpload>;
export type PresignedFileDownloadResponse = ApiSuccess<PresignedFileDownload>;
export type PublicFileResponse = ApiSuccess<PublicFile>;
export type PublicFileListResponse = ApiSuccess<PublicFile[]>;
